<template>
  <div class="container-fluid mt-4" dir="rtl">
    
    <!-- 1. لوحة التحكم (الفلاتر والتبويبات) -->
    <div class="card shadow-sm mb-4 border-top-info no-print">
      <div class="card-header bg-white py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h5 class="mb-0 text-info fw-bold">
          <i class="fas fa-chart-line ms-2"></i> التقارير الإحصائية المجمعة
        </h5>
        
        <!-- أزرار التبويبات -->
        <div class="btn-group" role="group">
            <button @click="switchTab('frequency')" class="btn" :class="currentTab === 'frequency' ? 'btn-info text-white' : 'btn-outline-info'">المجموع التكراري</button>
            <button @click="switchTab('top')" class="btn" :class="currentTab === 'top' ? 'btn-info text-white' : 'btn-outline-info'">أوائل الطلبة</button>
            <button @click="switchTab('subjects')" class="btn" :class="currentTab === 'subjects' ? 'btn-info text-white' : 'btn-outline-info'">إحصاء المواد</button>
        </div>
      </div>

      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-3">
            <label class="form-label fw-bold">القطاع / الإدارة</label>
            <select v-model="filters.sector" class="form-select">
              <option value="">-- كل القطاعات --</option>
              <option value="1">قطاع العريش</option>
              <option value="2">قطاع بئر العبد</option>
              <option value="3">قطاع الشيخ زويد/رفح</option>
              <option value="4">قطاع وسط سيناء</option>
            </select>
          </div>
          
          <div class="col-md-3">
             <label class="form-label fw-bold">نوع الطلاب</label>
             <select v-model="filters.gender" class="form-select">
               <option value="">بنين وبنات</option>
               <option value="1">بنين فقط</option>
               <option value="2">بنات فقط</option>
             </select>
          </div>

          <div class="col-md-3">
            <button @click="fetchData" class="btn btn-dark w-100 fw-bold" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm ms-2"></span>
              <span v-else><i class="fas fa-sync-alt ms-2"></i> عرض التقرير</span>
            </button>
          </div>
          
          <div class="col-md-3">
             <button @click="printPage" class="btn btn-secondary w-100">
               <i class="fas fa-print ms-2"></i> طباعة
             </button>
          </div>
        </div>
      </div>
    </div>

    <!-- منطقة الطباعة -->
    <div id="printMe" class="min-vh-100 bg-white p-3 rounded">
      
      <!-- ============================================= -->
      <!-- 1. تبويب المجموع التكراري -->
      <!-- ============================================= -->
      <div v-if="currentTab === 'frequency' && stats" class="report-section animate__animated animate__fadeIn">
        <div class="text-center mb-4 pt-2 border-bottom pb-3">
           <h3 class="fw-bold text-decoration-underline">تقرير المجموع التكراري والنسب المئوية</h3>
           <h5 class="text-muted">الشهادة الإعدادية - الدور الأول 2026</h5>
        </div>

        <!-- الملخص العام -->
        <div class="row g-3 text-center mb-4">
            <div class="col-3">
                <div class="stat-box p-3 bg-light border border-dark rounded">
                    <small class="fw-bold text-muted">المتقدمين</small>
                    <h3 class="fw-bold m-0">{{ stats.total }}</h3>
                </div>
            </div>
            <div class="col-3">
                <div class="stat-box p-3 bg-success-subtle border border-success rounded">
                    <small class="fw-bold text-success">الناجحين</small>
                    <h3 class="fw-bold text-success m-0">{{ stats.pass }}</h3>
                </div>
            </div>
            <div class="col-3">
                <div class="stat-box p-3 bg-danger-subtle border border-danger rounded">
                    <small class="fw-bold text-danger">راسب / دور ثان</small>
                    <h3 class="fw-bold text-danger m-0">{{ stats.fail }}</h3>
                </div>
            </div>
            <div class="col-3">
                <div class="stat-box p-3 bg-dark text-white rounded">
                    <small class="opacity-75">نسبة النجاح</small>
                    <h3 class="fw-bold text-warning m-0">{{ stats.percentage }}%</h3>
                </div>
            </div>
        </div>

        <div class="row">
            <!-- الجدول -->
            <div class="col-lg-7">
                <table class="table table-bordered table-striped text-center align-middle mb-0">
                    <thead class="table-dark">
                        <tr><th>م</th><th>فئة الدرجة</th><th>التقدير</th><th>العدد</th><th>النسبة</th></tr>
                    </thead>
                    <tbody>
                        <tr v-for="(row, i) in distribution" :key="i">
                            <td>{{ i + 1 }}</td>
                            <td class="fw-bold" dir="ltr">{{ row.range_label }}</td>
                            <td><span class="badge" :class="getBadgeClass(row.label)">{{ row.label }}</span></td>
                            <td class="fs-5 fw-bold">{{ row.count }}</td>
                            <td>{{ row.percent }}%</td>
                        </tr>
                    </tbody>
                    <tfoot class="fw-bold bg-light">
                        <tr><td colspan="3">الإجمالي</td><td>{{ stats.total }}</td><td>100%</td></tr>
                    </tfoot>
                </table>
            </div>
            
            <!-- الرسم البياني (يختفي في الطباعة) -->
            <div class="col-lg-5 ">
                <div class="card h-100 shadow-sm border-0">
                    <div class="card-body">
                        <GradesChart v-if="chartData.labels.length > 0" :chartData="chartData" />
                    </div>
                </div>
            </div>
        </div>
      </div>

      <!-- ============================================= -->
      <!-- 2. تبويب أوائل الطلبة -->
      <!-- ============================================= -->
      <div v-if="currentTab === 'top' && topStudents.length > 0" class="report-section animate__animated animate__fadeIn">
        <div class="text-center mb-4 pt-2">
           <h3 class="fw-bold text-success text-decoration-underline">
               <i class="fas fa-crown text-warning me-2"></i>كشف أوائل الطلبة (العشرة الأوائل)
           </h3>
           <h6 class="text-muted">مرتبة تنازلياً حسب المجموع الكلي ثم تاريخ الميلاد (الأصغر سناً)</h6>
        </div>

        <table class="table table-bordered border-dark text-center align-middle">
            <thead class="bg-success text-white">
                <tr>
                    <th width="5%">م</th>
                    <th width="10%">جلوس</th>
                    <th width="35%">اسم الطالب</th>
                    <th width="20%">المدرسة</th>
                    <th width="10%">المجموع</th>
                    <th width="10%">النسبة</th>
                    <th width="10%">الترتيب</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(student, idx) in topStudents" :key="student.id" :class="idx < 3 ? 'bg-warning-subtle' : ''">
                    <td>{{ idx + 1 }}</td>
                    <td class="fw-bold">{{ student.Seating_No_1 }}</td>
                    <td class="text-start fw-bold text-primary pe-3">{{ student.Arabic_Name }}</td>
                    <td>{{ student.School_Name }}</td>
                    <td class="bg-white fw-bold fs-5 text-dark border">{{ student.Total_Degree_1 }}</td>
                    <td class="fw-bold">{{ calculatePercentage(student.Total_Degree_1) }}%</td>
                    <td>
                        <span v-if="idx === 0" class="badge bg-gold text-dark fs-6 shadow-sm">الأول</span>
                        <span v-else-if="idx === 1" class="badge bg-secondary text-white">الثاني</span>
                        <span v-else-if="idx === 2" class="badge bg-brown text-white">الثالث</span>
                        <span v-else class="fw-bold">{{ idx + 1 }}</span>
                    </td>
                </tr>
            </tbody>
        </table>
      </div>

      <!-- ============================================= -->
      <!-- 3. تبويب إحصاء المواد -->
      <!-- ============================================= -->
      <div v-if="currentTab === 'subjects' && subjectsStats.length > 0" class="report-section animate__animated animate__fadeIn">
        <div class="text-center mb-4 pt-2">
           <h3 class="fw-bold text-primary text-decoration-underline">الإحصاء التحليلي لنتائج المواد الدراسية</h3>
        </div>

        <div class="table-responsive">
            <table class="table table-bordered table-hover text-center align-middle border-dark">
                <thead class="bg-primary text-white">
                    <tr>
                        <th rowspan="2">المادة</th>
                        <th rowspan="2">العظمى</th>
                        <th rowspan="2">متقدم</th>
                        <th colspan="2" class="bg-success border-white">ناجح</th>
                        <th colspan="2" class="bg-danger border-white">راسب</th>
                        <th rowspan="2">المتوسط</th>
                    </tr>
                    <tr>
                        <th class="bg-success border-white">عدد</th>
                        <th class="bg-success border-white">%</th>
                        <th class="bg-danger border-white">عدد</th>
                        <th class="bg-danger border-white">%</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="sub in subjectsStats" :key="sub.id">
                        <td class="fw-bold bg-light text-start ps-3">{{ sub.name }}</td>
                        <td class="fw-bold">{{ sub.max_score }}</td>
                        <td>{{ sub.total_students }}</td>
                        
                        <!-- الناجح -->
                        <td class="text-success fw-bold fs-5">{{ sub.pass_count }}</td>
                        <td style="width: 15%">
                            <div class="progress position-relative" style="height: 20px;">
                                <div class="progress-bar bg-success" :style="{width: sub.pass_percent + '%'}"></div>
                                <span class="position-absolute w-100 text-center text-dark fw-bold small" style="top: 0;">{{ sub.pass_percent }}%</span>
                            </div>
                        </td>

                        <!-- الراسب -->
                        <td class="text-danger fw-bold">{{ sub.fail_count }}</td>
                        <td class="text-muted small">{{ (100 - sub.pass_percent).toFixed(1) }}%</td>

                        <!-- المتوسط -->
                        <td class="fw-bold text-primary">{{ sub.average }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
      </div>

      <!-- رسالة التحميل أو الفراغ -->
      <div v-if="loading" class="text-center py-5 no-print">
          <div class="spinner-border text-primary" role="status"></div>
          <p class="mt-2 text-muted">جاري تحميل البيانات...</p>
      </div>

      <div v-if="!loading && isEmptyState" class="alert alert-info text-center mt-4 no-print">
          لا توجد بيانات للعرض، يرجى التأكد من الفلاتر أو الضغط على "عرض التقرير".
      </div>

      <!-- تذييل الطباعة -->
      <div class="mt-5 pt-5 d-none d-print-block">
          <div class="d-flex justify-content-between px-5 fw-bold text-center">
              <div>مدير الإحصاء</div>
              <div>رئيس لجنة النظام والمراقبة</div>
              <div>وكيل الوزارة</div>
          </div>
      </div>

    </div>
  </div>
</template>

<script>
import api from '@/api/axios';
import GradesChart from '@/components/GradesChart.vue'; // تأكد من المسار

export default {
  name: "AdvancedReports",
  components: { GradesChart },
  data() {
    return {
      currentTab: 'frequency',
      loading: false,
      filters: { sector: '', gender: '' },
      
      // 1. بيانات التكراري
      stats: null,
      distribution: [],
      chartData: { labels: [], datasets: [] },

      // 2. بيانات الأوائل
      topStudents: [],
      maxTotalScore: 280, // درجة المجموع الكلي (للحساب)

      // 3. بيانات المواد
      subjectsStats: []
    };
  },
  computed: {
    isEmptyState() {
        if (this.currentTab === 'frequency') return !this.stats;
        if (this.currentTab === 'top') return this.topStudents.length === 0;
        if (this.currentTab === 'subjects') return this.subjectsStats.length === 0;
        return false;
    }
  },
  methods: {
    // التبديل بين التبويبات مع تحديث البيانات
    switchTab(tab) {
        this.currentTab = tab;
        // خيار: جلب البيانات تلقائياً عند التبديل
        this.fetchData(); 
    },

    async fetchData() {
      this.loading = true;
      // تصفير البيانات القديمة لتجنب الخلط
      this.stats = null;
      this.topStudents = [];
      this.subjectsStats = [];

      try {
        let endpoint = '';
        if (this.currentTab === 'frequency') endpoint = '/admin/stats/frequency';
        else if (this.currentTab === 'top') endpoint = '/admin/stats/top-students';
        else if (this.currentTab === 'subjects') endpoint = '/admin/stats/subjects';

        const res = await api.get(endpoint, { params: this.filters });

        // تخزين البيانات حسب التبويب
        if (this.currentTab === 'frequency') {
            this.stats = res.data.stats;
            this.distribution = res.data.distribution;
            this.prepareChartData();
        } else if (this.currentTab === 'top') {
            this.topStudents = res.data;
        } else if (this.currentTab === 'subjects') {
            this.subjectsStats = res.data;
        }

      } catch (e) {
        console.error("Error fetching report:", e);
        // alert("حدث خطأ في الاتصال بالسيرفر");
      } finally {
        this.loading = false;
      }
    },

    // تجهيز بيانات الشارت
    prepareChartData() {
      if (!this.distribution.length) return;
      
      const labels = this.distribution.map(item => item.label.split(' ')[0]); // نأخذ الكلمة الأولى فقط (ممتاز)
      const data = this.distribution.map(item => item.count);
      const colors = ['#198754', '#0d6efd', '#0dcaf0', '#ffc107', '#dc3545'];

      this.chartData = {
        labels: labels,
        datasets: [{
          label: 'عدد الطلاب',
          data: data,
          backgroundColor: colors,
          borderRadius: 5,
          barThickness: 40
        }]
      };
    },

    // دوال مساعدة
    getBadgeClass(label) {
        if(label.includes('امتياز')) return 'bg-success';
        if(label.includes('جيد جدا')) return 'bg-primary';
        if(label.includes('جيد')) return 'bg-info text-dark';
        if(label.includes('مقبول')) return 'bg-warning text-dark';
        return 'bg-danger';
    },

    calculatePercentage(score) {
        return ((score / this.maxTotalScore) * 100).toFixed(1);
    },

    printPage() {
        this.$htmlToPaper("printMe");
    }
  }
};
</script>

<style scoped>
.border-top-info { border-top: 4px solid #0dcaf0; }
.bg-gold { background-color: #ffd700; }
.bg-brown { background-color: #cd7f32; } /* للون البرونزي */

@media print {
    .no-print { display: none !important; }
    .d-print-block { display: block !important; }
    
    /* تنسيقات الجدول في الطباعة */
    table { width: 100% !important; font-size: 11pt; page-break-inside: auto; }
    tr { page-break-inside: avoid; page-break-after: auto; }
    th, td { border: 1px solid #000 !important; padding: 5px !important; }
    
    /* إجبار الألوان */
    .bg-success, .bg-danger, .progress-bar, .badge {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }
    .stat-box { border: 1px solid #000; }
}
</style>